/**
 * Read-only header for the RMA processing form: identity, scope links
 * (customer / site-group / structure / ticket), and the audit timeline
 * (issued / shipped / processed / invoiced / updated).
 *
 * Rendered above the Formik body; purely informational.
 *
 * Contains:
 * - RmaDetailsSection — the titled details card (default export)
 */

import { Box } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { Map } from 'immutable';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatUserString } from '@sophocles/utils';
import TextDisplay from '@sophocles/views/returns/components/TextDisplay';

import { SectionCard } from '../../components/ReturnSection';

function fmtDate(isoString) {
  if (!isoString) return null;
  try {
    return format(parseISO(isoString), 'M/d/yyyy');
  } catch {
    return isoString;
  }
}

function fmtDateTime(isoString) {
  if (!isoString) return null;
  try {
    return format(parseISO(isoString), 'M/d/yyyy, h:mm:ss aa');
  } catch {
    return isoString;
  }
}

export default function RmaDetailsSection({ rma, entityLabels, isStaff }) {
  const { t } = useTranslation();
  const rmaStatusDisplay = rma.get('status_display', '');

  const customer = rma.getIn(['site_group', 'customer'], Map());
  const customerId = customer ? customer.get('id', 0) : 0;
  const siteGroup = rma.get('site_group', Map());
  const siteGroupId = siteGroup ? siteGroup.get('id', 0) : 0;
  const site = rma.get('site', null);
  const ticket = rma.get('ticket', null);

  const issueTime = rma.get('input_time', null);
  const updateTime = rma.get('update_time', null);
  const shippingDate = rma.get('shipping_date', null);
  const receivedDate = rma.get('received_date', null);
  const invoiceDate = rma.get('invoiced_time', null);
  const issueUser = rma.get('input_user', null);
  const updateUser = rma.get('update_user', null);
  const shippingUser = rma.get('shipping_user', null);
  const receivedUser = rma.get('received_user', null);
  const invoiceUser = rma.get('invoiced_user', null);

  return (
    <SectionCard title={t('rma.rmaDetails', 'RMA Details')}>
      <Box component="dl" sx={{ m: 0 }}>
        <TextDisplay title={t('rma.status', 'Status')} show={!!rmaStatusDisplay}>
          {rmaStatusDisplay}
        </TextDisplay>
        <TextDisplay title={entityLabels.customer} show={!!customer}>
          {customerId}. {customer.get('name', t('rma.noneLower', 'none'))}{' '}
          <Link style={{ fontSize: '13px' }} target="_blank" to={`/c/${customerId}/plants`}>
            {t('rma.link', '(link)')}
          </Link>
        </TextDisplay>
        <TextDisplay title={entityLabels.siteGroup} show={!!siteGroup}>
          {siteGroup.get('name', '0')}. {siteGroup.get('description', t('rma.noneLower', 'none'))}{' '}
          <Link style={{ fontSize: '13px' }} target="_blank" to={`/c/${customerId}/plant/${siteGroupId}/structures`}>
            {t('rma.link', '(link)')}
          </Link>
        </TextDisplay>
        {!!site && (
          <TextDisplay title={entityLabels.structure}>
            {site.get('name', '0')}. {site.get('description', t('rma.noneLower', 'none'))}{' '}
            <Link
              style={{ fontSize: '13px' }}
              target="_blank"
              to={`/c/${customerId}/plant/${siteGroupId}/structure/${site.get('id', 0)}`}
            >
              {t('rma.link', '(link)')}
            </Link>
          </TextDisplay>
        )}
        {!!ticket && (
          <TextDisplay title={t('rma.supportTicket', 'Support Ticket')}>
            {ticket.get('ticket_number', '')}{' '}
            <Link style={{ fontSize: '13px' }} target="_blank" to={`/support/ticket/${ticket.get('id')}/view`}>
              {t('rma.link', '(link)')}
            </Link>
          </TextDisplay>
        )}
        {issueTime && (
          <TextDisplay title={t('rma.timeIssuedFull', 'Time Issued')}>
            {fmtDateTime(issueTime)} {isStaff ? ' - ' + formatUserString(issueUser) : null}
          </TextDisplay>
        )}
        {shippingDate && (
          <TextDisplay title={t('rma.dateShippedFull', 'Date Shipped')}>
            {fmtDate(shippingDate)} {isStaff ? ' - ' + formatUserString(shippingUser) : null}
          </TextDisplay>
        )}
        {receivedDate && (
          <TextDisplay title={t('rma.dateProcessedFull', 'Date Processed')}>
            {fmtDate(receivedDate)} {isStaff ? ' - ' + formatUserString(receivedUser) : null}
          </TextDisplay>
        )}
        {invoiceDate && isStaff && (
          <TextDisplay title={t('rma.dateInvoicedStar', 'Date Invoiced*')}>
            {fmtDate(invoiceDate)} - {formatUserString(invoiceUser)}
          </TextDisplay>
        )}
        {updateTime && isStaff && (
          <TextDisplay title={t('rma.lastUpdatedStar', 'Last Updated*')}>
            {fmtDateTime(updateTime)} - {formatUserString(updateUser)}
          </TextDisplay>
        )}
      </Box>
    </SectionCard>
  );
}
